import { Link } from 'react-router-dom';
import { Card, Badge } from '@/components/ui';
import { Thermometer, Snowflake, RefrigeratorIcon, Package } from 'lucide-react';
import type { DashboardStats } from './types';

interface ZonesGridProps {
  stats: DashboardStats;
}

interface ZoneConfig {
  key: keyof Pick<
    DashboardStats,
    'positiveZone' | 'negativeZone' | 'frigoZone' | 'reserveZone' | 'caveZone' | 'entretienZone'
  >;
  name: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  border: string;
  badgeVariant: 'info' | 'warning' | 'neutral';
}

const zones: ZoneConfig[] = [
  {
    key: 'positiveZone',
    name: 'Zone Positive',
    description: 'Température ambiante',
    icon: <Thermometer className="text-white" size={20} />,
    gradient: 'from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30',
    border: 'border-blue-200 dark:border-blue-700',
    badgeVariant: 'info',
  },
  {
    key: 'negativeZone',
    name: 'Zone Négative',
    description: 'Surgelés (-18°C)',
    icon: <Snowflake className="text-white" size={20} />,
    gradient: 'from-cyan-50 to-cyan-100 dark:from-cyan-900/30 dark:to-cyan-800/30',
    border: 'border-cyan-200 dark:border-cyan-700',
    badgeVariant: 'info',
  },
  {
    key: 'frigoZone',
    name: 'Frigo',
    description: 'Réfrigéré (0-4°C)',
    icon: <RefrigeratorIcon className="text-white" size={20} />,
    gradient: 'from-teal-50 to-teal-100 dark:from-teal-900/30 dark:to-teal-800/30',
    border: 'border-teal-200 dark:border-teal-700',
    badgeVariant: 'info',
  },
  {
    key: 'reserveZone',
    name: 'Réserve',
    description: 'Produits secs',
    icon: <Package className="text-white" size={20} />,
    gradient: 'from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30',
    border: 'border-amber-200 dark:border-amber-700',
    badgeVariant: 'warning',
  },
  {
    key: 'caveZone',
    name: 'Cave',
    description: 'Boissons et alcools',
    icon: <Package className="text-white" size={20} />,
    gradient: 'from-rose-50 to-rose-100 dark:from-rose-900/30 dark:to-rose-800/30',
    border: 'border-rose-200 dark:border-rose-700',
    badgeVariant: 'info',
  },
  {
    key: 'entretienZone',
    name: 'Entretien',
    description: 'Fournitures',
    icon: <Package className="text-white" size={20} />,
    gradient: 'from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30',
    border: 'border-purple-200 dark:border-purple-700',
    badgeVariant: 'neutral',
  },
];

const iconBgColors: Record<string, string> = {
  positiveZone: 'bg-blue-500',
  negativeZone: 'bg-cyan-500',
  frigoZone: 'bg-teal-500',
  reserveZone: 'bg-amber-500',
  caveZone: 'bg-rose-500',
  entretienZone: 'bg-purple-500',
};

export function ZonesGrid({ stats }: ZonesGridProps) {
  return (
    <div className="mb-8">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
        Zones de stockage
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 lg:gap-6">
        {zones.map((zone) => (
          <Link key={zone.key} to="/inventory">
            <Card
              padding="sm"
              hoverable
              className={`bg-gradient-to-br ${zone.gradient} ${zone.border} h-full`}
            >
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-4">
                <div className={`p-2 sm:p-3 ${iconBgColors[zone.key]} rounded-lg`}>
                  {zone.icon}
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-xs sm:text-base">
                    {zone.name}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 hidden sm:block mb-2">
                    {zone.description}
                  </p>
                  <Badge variant={zone.badgeVariant} size="sm">
                    {stats[zone.key]}
                  </Badge>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
